import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild}       from '@angular/core';
import {
  ApDynComponentComponent
}                                                                     from '../../ap-dyncomponent/ap-dyncomponent.component';
import {
  IPpEntryPlanBookingGridItem
}                                                                     from './plan-booking/pp-plan-booking.types';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerWizardButton,
  ApDynGridPagerWizardConfig,
  ApDynGridPagerWizardSection
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-pager-wizard-config';
import {
  FormStore
}                                                                     from '../../stores/layout/form.store';
import {
  FieldStore
}                                                                     from '../../stores/farm/field.store';
import {
  DriverStore
}                                                                     from '../../stores/docu/driver.store';
import {
  MapViewStore
}                                                                     from '../../stores/layout/mapview.store';
import {
  MachineStore
}                                                                     from '../../stores/docu/machine.store';
import {
  SettingsStore
}                                                                     from '../../stores/base-data/settings.store';
import {
  LanguageStore
}                                                                     from '../../stores/translation/language.store';
import {
  CropTypeStore
}                                                                     from '../../stores/base-data/crop.types.store';
import {
  InstrumentStore
}                                                                     from '../../stores/docu/instrument.store';
import {
  ApDateService
}                                                                     from '../../ap-core/services/ap-date-service';
import {
  PpCommonService
}                                                                     from '../../plant-protection/services/pp-common.service';
import {
  ApNutrientService
}                                                                     from '../../ap-utils/service/ap-nutrient.service';
import {
  ApTranslationService
}                                                                     from '../../ap-utils/service/ap-translation.service';
import {
  GetRoundNumericService
}                                                                     from '../../ap-utils/service/get-round-numeric.service';
import {GetRoundNumericPipe}                                          from '../../ap-utils';
import {
  PpNoDataValue
}                                                                     from '../../plant-protection/types/pp-planning-booking.types';
import {
  ApUtilService
}                                                                     from '../../ap-utils/service/ap-util.service';
import {
  nameOf
}                                                                     from '../../ap-core/utils/ap-name-of';
import {
  ApDynGridPropertyColumnConfig,
  FilterType,
  PropertyTemplate
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridGroupColumnConfig
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {filter, map}                                                  from 'rxjs/operators';
import {MapViewMode}                                                  from '../../ap-interface';
import {
  ApDynGridStatusColumnConfig
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  IPpEntryPlanBookingShowGridItem,
  IPpPlanBookingShowComponentData
}                                                                     from './plan-booking/pp-plan-booking-show.types';

@Component({
  selector: 'pp-plan-booking-show',
  template: `
    <ap-dyncomponent [caption]="'Global__Show'"
                     [headerIcon]="'ap-fungidetect-icon'"
                     [loading$]="loading$"
                     [columns]="columns$ | async"
                     [items]="items$ | async"
                     [pager]="pager">
    </ap-dyncomponent>
  `,
  styles: []
})
export class PpPlanBookingShowComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) public dynComponent: ApDynComponentComponent;
  public data: IPpPlanBookingShowComponentData;
  public items$ = new BehaviorSubject<IPpEntryPlanBookingShowGridItem[]>([]);
  public columns$: Observable<ApDynGridColumnConfigBase[]> = of([]);
  public loading$: Observable<boolean> = of(false);
  public pager: ApDynGridPagerWizardConfig;

  private _subscriptions: Subscription[] = [];

  constructor(private formStore: FormStore,
              private fieldStore: FieldStore,
              private driverStore: DriverStore,
              private mapViewStore: MapViewStore,
              private machineStore: MachineStore,
              private settingsStore: SettingsStore,
              private languageStore: LanguageStore,
              private cropTypeStore: CropTypeStore,
              private instrumentStore: InstrumentStore,
              private dateService: ApDateService,
              private ppCommonService: PpCommonService,
              private nutrientService: ApNutrientService,
              private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private roundNumericPipe: GetRoundNumericPipe) {
  }

  public ngOnInit(): void {
    this.loading$ = this._getGridLoading();
    this.columns$ = this._getGridColumns();
    this.pager = this._getGridPager();
    this._setGridItems();
  }

  public ngAfterViewInit(): void {
    this._subscriptions.push(
      this.dynComponent.SelectedItems$.subscribe((items: IPpEntryPlanBookingShowGridItem[]) => {
        const fieldsToSelect: string[] = items.filter(x => x.FieldId).map(i => i.FieldId.toString());
        this.fieldStore.changeSelectedField(fieldsToSelect);
      })
    );
    this._subscriptions.push(
      this.items$.subscribe((items: IPpEntryPlanBookingShowGridItem[]): void => {
        const itemsToSelect = items.map(i => i.Id);
        setTimeout(() => this.dynComponent.dynGrid.gridPaging.setSelectedKeys(itemsToSelect));
      })
    );
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  private _getGridLoading(): Observable<boolean> {
    return combineLatest([
      this.fieldStore.Listen(x => x.loading),
      this.settingsStore.Listen(x => x.loading),
      this.languageStore.Listen(x => x.loading),
      this.driverStore.Listen(x => x.loading),
      this.instrumentStore.Listen(x => x.loading),
      this.machineStore.Listen(x => x.loading)
    ]).pipe(
      map((loadings) => loadings.some((x) => x))
    );
  }

  private _getGridColumns(): Observable<ApDynGridColumnConfigBase[]> {
    return combineLatest([
      this.languageStore.SelectedLanguage$,
      this.settingsStore.FirstSetting$
    ]).pipe(
      filter(([language, settings]) => !!language && !!settings),
      map(([language, settings]) => [
        new ApDynGridStatusColumnConfig({
          field: nameOf<IPpEntryPlanBookingGridItem>('Status'),
          class: nameOf<IPpEntryPlanBookingGridItem>('StatusClass'),
          description: nameOf<IPpEntryPlanBookingGridItem>('StatusDesc'),
          title: '',
          filterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Docu_Ini__Number',
          field: `${nameOf<IPpEntryPlanBookingGridItem>('FieldNumber')}`,
          hide: true,
          width: 40,
          cssClass: 'right',
          filterType: FilterType.NUMBER,
          headerFilterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Docu_Ini_Subnumber',
          field: `${nameOf<IPpEntryPlanBookingGridItem>('FieldSubnumber')}`,
          width: 40,
          hide: true,
          filterType: FilterType.NUMBER,
          cssClass: 'right',
          headerFilterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Docu_Ini__Number',
          field: `${nameOf<IPpEntryPlanBookingGridItem>('FieldNumberWithSubnumber')}`,
          hide: new ApDynGridColumnHideConfig({
            mapHide: false,
            selfHide: this.mapViewStore.Listen(s => s.mode).pipe(map((m) => m !== MapViewMode.NORMAL)),
          }),
          width: 50,
          headerFilterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Docu_Ini__Name',
          field: `${nameOf<IPpEntryPlanBookingGridItem>('FieldName')}`,
          width: 110,
          cssClass: 'left',
          sortIndex: 0,
          headerFilterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Docu_Ini__Area',
          field: `${nameOf<IPpEntryPlanBookingGridItem>('Area')}`,
          width: 50,
          headerFilterable: true,
          tooltip: (dataItem: IPpEntryPlanBookingGridItem) => this.roundNumericService.stringify(dataItem?.Area ?? 0, 4),
          cssClass: 'right',
          filterType: FilterType.NUMBER,
          pipes: [{
            pipe: this.roundNumericPipe,
            args: [settings.DigitsAfterDecimalPoint]
          }]
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Global__Crop',
          field: `${nameOf<IPpEntryPlanBookingGridItem>('Crop')}`,
          width: 80,
          hide: true,
          headerFilterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Global__EC',
          field: `${nameOf<IPpEntryPlanBookingGridItem>('EcValue')}`,
          width: 50,
          hide: true,
          cssClass: 'right',
          filterType: FilterType.NUMBER
        }),
        new ApDynGridGroupColumnConfig({
          title: 'Global__Product',
          groupColumns: [
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Avg_Symbol',
              field: `${nameOf<IPpEntryPlanBookingGridItem>('ProductAverages')}`,
              tooltip: (dataItem: IPpEntryPlanBookingGridItem) => dataItem?.ProductAveragesTooltip,
              width: 150,
              multiline: true,
              filterable: true
            }),
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Sum',
              field: `${nameOf<IPpEntryPlanBookingGridItem>('ProductSum')}`,
              tooltip: (dataItem: IPpEntryPlanBookingGridItem) => dataItem?.ProductSumTooltip,
              width: 80,
              multiline: true,
              filterable: true
            })
          ]
        }),
        new ApDynGridGroupColumnConfig({
          title: 'Global__Water',
          hide: true,
          groupColumns: [
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Avg_Symbol',
              field: `${nameOf<IPpEntryPlanBookingGridItem>('WaterRate')}`,
              hide: true,
              width: 60,
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              template: PropertyTemplate.NUMBER,
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue, undefined], ReplaceWith: ''}]
              }]
            }),
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Sum',
              field: `${nameOf<IPpEntryPlanBookingGridItem>('WaterSum')}`,
              tooltip: (dataItem: IPpEntryPlanBookingGridItem) => dataItem?.WaterSumTooltip,
              hide: true,
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              template: PropertyTemplate.NUMBER,
              width: 60,
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue, undefined], ReplaceWith: ''}]
              }]
            })
          ]
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Global_Application_Date',
          field: `${nameOf<IPpEntryPlanBookingGridItem>('ApplicationDate')}`,
          hide: true,
          width: 80,
          filterable: true,
          format: language.DateFormat,
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Duration',
          field: `${nameOf<IPpEntryPlanBookingGridItem>('Duration')}`,
          format: 'HH:mm',
          hide: true,
          width: 80,
          filterable: true
        }),
        new ApDynGridGroupColumnConfig({
          title: 'agrioptions_resources',
          hide: true,
          groupColumns: [
            new ApDynGridPropertyColumnConfig({
              title: 'Base__Engine',
              field: 'Machine.NameWithSign',
              hide: true,
              width: 100
            }),
            new ApDynGridPropertyColumnConfig({
              title: 'AgriConnect_Ini__Tool',
              field: 'Instrument.Name',
              hide: true,
              width: 100
            }),
            new ApDynGridPropertyColumnConfig({
              title: 'Docu_Ini__Motorist',
              field: 'Driver.ShortFullName',
              hide: true,
              width: 100
            })
          ]
        })
      ])
    );
  }

  private _getGridPager(): ApDynGridPagerWizardConfig {
    return new ApDynGridPagerWizardConfig(
      new ApDynGridPagerWizardButton('Global__Cancel', ApUtilService.createEventEmitter(this._onClose.bind(this), this._subscriptions)),
      new ApDynGridPagerWizardSection(),
      new ApDynGridPagerWizardSection()
    );
  }

  private _setGridItems(): void {
    const gridItems: IPpEntryPlanBookingShowGridItem[] = this.data.DataItem?.filter(x => !!x.BookedBy).map(planBook => {
      const field = this.fieldStore.getFieldById(planBook.FieldId);
      const geom = this.fieldStore.getFieldGeomById(planBook.FieldGeomId);
      const driver = this.driverStore.getDriverById(planBook.ExportDriver);
      const machine = this.machineStore.getMachineById(planBook.ExportMachine);
      const instrument = this.instrumentStore.getInstrumentById(planBook.ExportInstrument);
      const isWorkTypeSpraying = this.ppCommonService.isWorkTypeSpraying(planBook);
      const waterSum = isWorkTypeSpraying ? this.ppCommonService.getWaterAmountBasedOnOperationMode(planBook) : PpNoDataValue;
      const waterUnit = isWorkTypeSpraying ? this.translationService.translate('Api_Enums__Litre') : undefined;
      const area = geom?.AreaHa ?? 0;
      const item: IPpEntryPlanBookingShowGridItem = {
        Id: planBook.Id,
        Status: this.nutrientService.getFertilizerStatus(planBook.ApplicationMode, true)?.state,
        StatusClass: this.nutrientService.getFertilizerStatus(planBook.ApplicationMode, true)?.class,
        StatusDesc: this.nutrientService.getFertilizerStatusDescription(planBook.ApplicationMode, true),
        FieldId: field?.Id,
        FieldName: field?.FieldName,
        FieldNumber: field?.FieldNumber,
        FieldSubnumber: field?.FieldSubnumber,
        FieldNumberWithSubnumber: this.fieldStore.getFieldNumberAndSubNumber(field),
        Area: geom?.AreaHa,
        EcValue: planBook.EcValue,
        Crop: this.cropTypeStore.getDefaultCroptype(planBook.MainCropId)?.Description ?? '',
        ProductAverages: this.ppCommonService.getAppliedProductsExpr(planBook.ProductList),
        ProductAveragesTooltip: this.ppCommonService.getAppliedProductsExpr(planBook.ProductList, true),
        ProductSum: isWorkTypeSpraying ? this.ppCommonService.getAppliedProductSumExpr(planBook.ProductList, {
          areaHa: area,
          withoutProdName: true
        }) : undefined,
        ProductSumTooltip: isWorkTypeSpraying ? this.ppCommonService.getAppliedProductSumExpr(planBook.ProductList, {
          areaHa: area,
          isTooltip: true
        }) : undefined,
        WaterRate: isWorkTypeSpraying ? this.ppCommonService.getApplicationValue(planBook) : PpNoDataValue,
        WaterSum: isWorkTypeSpraying ? this.ppCommonService.getWaterAmountBasedOnOperationMode(planBook) : PpNoDataValue,
        WaterSumTooltip: isWorkTypeSpraying && waterSum >= 0 ? `${waterSum ?? 0} ${waterUnit}` : '',
        ApplicationDate: this.dateService.toFarmDate(planBook.ApplicationDate).toDate(),
        Duration: this.dateService.getDateWithTimeFromMilliseconds(planBook.TEnd?.TotalMilliseconds ?? 0),
        Driver: driver,
        Machine: machine,
        Instrument: instrument
      };
      return item;
    });
    setTimeout(() => this.items$.next(gridItems), 10);
  }

  private _onClose(): void {
    this.formStore.closeForm();
  }
}
